<template>
  <SupportBanner />
  <header>
    <nav
      id="app-header"
      class="navbar"
      role="navigation"
      aria-label="Main navigation"
    >
      <a
        href="#skip-to-main-content"
        class="skip-link t--white t--uppercase t--bold t--caption"
        @click.prevent="focusMainContent"
      >
        Skip to main content
      </a>

      <div class="navbar-container">
        <div class="navbar-brand">
          <div>
            <a
              href="/"
              class="navbar-title navbar-item is-5 t--title-secondary"
              :title="accountName"
            >
              {{ accountName }}
            </a>
            <NameAndBadge id="nr-name-and-badge" />
          </div>
          <a
            href="#"
            role="button"
            class="navbar-burger"
            aria-label="menu"
            aria-expanded="false"
            @click.stop="handleToggleMenu"
          >
            <span aria-hidden="true"></span>
            <span aria-hidden="true"></span>
            <span aria-hidden="true"></span>
          </a>
        </div>

        <div class="navbar-menu" ref="menu">
          <div class="navbar-start">
            <ul>
              <li>
                <MakeRequestMenu
                  v-if="makeRequestMenuFirst"
                  :account="account"
                  :getNavbarItemClassName="getNavbarItemClassName"
                  :handleNavigate="handleNavigate"
                />
              </li>
              <li>
                <router-link
                  to="/requests"
                  v-slot="{ href, route, navigate }"
                  custom
                >
                  <div :class="getNavbarItemClassName(route)">
                    <a :href="href" @click="handleNavigate($event, navigate)">
                      All requests
                    </a>
                  </div>
                </router-link>
              </li>
              <li>
                <router-link
                  to="/documents"
                  v-slot="{ href, route, navigate }"
                  custom
                >
                  <div :class="getNavbarItemClassName(route)">
                    <a :href="href" @click="handleNavigate($event, navigate)">
                      Documents
                    </a>
                  </div>
                </router-link>
              </li>
              <li>
                <router-link
                  v-if="canReadStaffOnly"
                  to="/tasks"
                  v-slot="{ href, route }"
                  custom
                >
                  <div :class="getNavbarItemClassName(route)">
                    <a :href="href"> All tasks </a>
                  </div>
                </router-link>
              </li>
            </ul>
          </div>
          <div v-if="!makeRequestMenuFirst">
            <MakeRequestMenu
              :account="account"
              :getNavbarItemClassName="getNavbarItemClassName"
              :handleNavigate="handleNavigate"
            />
          </div>
          <div class="navbar-end">
            <template v-if="isAuthenticated">
              <template v-if="canManageInternalAdminTools">
                <div
                  class="navbar-item has-dropdown is-hoverable"
                  data-link="nr-internal"
                >
                  <span class="navbar-link">NR Internal</span>
                  <div class="navbar-dropdown is-right is-boxed">
                    <a href="/stats" class="navbar-item">Stats</a>
                    <a href="/internal/accounts" class="navbar-item"
                      >Customers</a
                    >
                    <a href="/internal/users" class="navbar-item">Users</a>
                    <a
                      href="/internal/support_banner_config"
                      class="navbar-item"
                      >Support Banner</a
                    >
                    <a href="/internal/dashboard" class="navbar-item"
                      >Superuser Admin Dashboard</a
                    >
                  </div>
                </div>
              </template>

              <template v-if="canReadAdminOnly">
                <div
                  class="navbar-item has-dropdown is-hoverable"
                  data-link="admin"
                >
                  <span
                    class="navbar-link is-arrowless navbar-icon-desktop admin-settings"
                  >
                    <NrIcon name="cog" class="icon" />
                  </span>
                  <span class="navbar-link navbar-icon-mobile admin-settings">
                    <NrIcon name="cog" class="icon" />
                    Admin settings
                  </span>
                  <div class="navbar-dropdown is-right is-boxed">
                    <template v-if="canManageAll">
                      <a href="/admin" class="navbar-item">Admin dashboard</a>
                    </template>
                    <template v-if="isSpa">
                      <router-link
                        v-if="canManageAll"
                        to="/admin/document_dashboard"
                        class="navbar-item"
                      >
                        Document dashboard
                      </router-link>
                    </template>
                    <template v-else>
                      <a
                        v-if="canManageAll"
                        href="/admin/document_dashboard"
                        class="navbar-item"
                      >
                        Document dashboard
                      </a>
                    </template>
                    <a href="/reports" class="navbar-item">Reports</a>

                    <hr class="navbar-divider" />
                    <a href="/admin/departments" class="navbar-item"
                      >Departments</a
                    >
                    <a href="/admin/users" class="navbar-item">Users</a>
                    <a href="/admin/request_text_filters" class="navbar-item">
                      Alerts
                    </a>
                    <a href="/admin/tags" class="navbar-item">Tags</a>
                    <a
                      v-if="account.uploaders_feature_flag"
                      href="/admin/onboarding"
                      class="navbar-item"
                    >
                      Upload users
                    </a>

                    <template
                      v-if="account.redaction || account.document_review"
                    >
                      <hr class="navbar-divider" />
                      <a href="/admin/redaction_reasons" class="navbar-item">
                        Redaction reasons
                      </a>
                      <a href="/admin/redaction_regexes" class="navbar-item">
                        Redaction patterns
                      </a>
                    </template>

                    <hr class="navbar-divider" />
                    <a href="/admin/message_templates" class="navbar-item">
                      Message templates
                    </a>
                    <a href="/admin/close_request_types" class="navbar-item">
                      Closure templates
                    </a>
                    <a
                      v-if="account.payments"
                      href="/admin/invoice_templates"
                      class="navbar-item"
                    >
                      Invoice templates
                    </a>
                    <a
                      v-if="account.payments"
                      href="/admin/invoice_charges"
                      class="navbar-item"
                    >
                      Invoice charges
                    </a>

                    <template v-if="canManageAll">
                      <hr class="navbar-divider" />
                      <a :href="accountSettingsPath" class="navbar-item">
                        Portal settings
                      </a>
                    </template>
                  </div>
                </div>
              </template>
              <span
                class="navbar-link is-arrowless navbar-desktop-app-switcher navbar-icon-desktop"
                v-if="account.civicplus_sso_feature_flag"
              >
                <div id="cp-appSwitcher"></div>
              </span>
              <span
                class="navbar-link mobile-tablet-only navbar-icon-mobile navbar-mobile-app-switcher is-arrowless"
                v-if="account.civicplus_sso_feature_flag"
              >
                <div id="cp-mobile-appSwitcher"></div>
                App switcher
              </span>
              <div
                class="navbar-item has-dropdown is-hoverable"
                data-link="profile"
              >
                <span
                  class="navbar-link is-arrowless navbar-user-icon navbar-icon-desktop"
                >
                  <NrIcon name="circle-user" class="icon" />
                </span>
                <span class="navbar-link navbar-user-icon navbar-icon-mobile">
                  <NrIcon name="circle-user" /> Signed in
                </span>
                <div class="navbar-dropdown is-right is-boxed">
                  <div class="navbar-item">
                    <p>
                      Signed in as
                      <strong>{{ user.email_or_name }}</strong>
                    </p>
                  </div>
                  <hr class="navbar-divider" />
                  <a
                    v-if="!permissionsPreviewUser"
                    :href="userSettingsPath"
                    class="navbar-item"
                    >My settings</a
                  >
                  <a
                    v-if="canPreviewUser"
                    @click="setPermissionModal"
                    class="navbar-item"
                  >
                    Permission preview
                  </a>

                  <a
                    href="/users/sign_out"
                    class="navbar-item"
                    @click.prevent.stop="handleSignoutClick"
                  >
                    Sign out
                  </a>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="navbar-item">
                <form
                  v-if="hasCivicplusSSOFeature"
                  action="/users/auth/openid_connect"
                  method="post"
                >
                  <input :name="csrfParam" :value="csrfToken" type="hidden" />
                  <input
                    type="Submit"
                    class="button is-primary"
                    value="Sign in"
                  />
                </form>
                <a
                  v-else
                  href="/users/sign_in"
                  class="button is-primary"
                  data-link="sign-in"
                >
                  Sign in
                </a>
              </div>
            </template>
          </div>
        </div>
      </div>
      <NrModal
        :class="`permission-modal ${!isSpa ? 'legacy-permission-modal' : ''}`"
        primaryButtonText="Set permissions"
        secondaryButtonText="Cancel"
        title="Set permission view"
        :hasSecondaryButton="true"
        :show="permissionModal"
        @on-primary-click="submitPermission"
        @on-secondary-click="resetPreviewModal"
        @on-close-click="resetPreviewModal"
      >
        <NrSelect
          v-model="selectedPreviewUsers"
          :options="filteredPreviewUsers"
          @open="searchPreviewUsers"
          @search="searchPreviewUsers"
          :filterable="false"
          :loading="previewFetchState.fetching"
          :hasMore="!previewFetchState.end"
          @load-more="loadMorePreviewUsers"
        >
          <template v-slot:label>
            <span>Select a user to impersonate</span>
          </template>
        </NrSelect>

        <p>
          By selecting this user, you can preview what the application looks
          like as this user, including which controls and content they can
          access. You can only select other users who have lower level
          permission roles in the application. Any action taken during the
          preview will still be attributed to your user account.
        </p>
      </NrModal>
    </nav>
    <div v-if="permissionsPreviewUser" class="preview-banner">
      <div class="preview-banner-title">
        <NrTooltip
          :label="`By selecting this user, you can preview what the application looks like as this user, including which controls and content they can access. You can only select other users who have lower level permission roles in the application. Any action taken during the preview will still be attributed to your user account.`"
        >
          <NrIcon
            class="m-right-8 m-left-8 m-bottom-4 inline-icon"
            name="info-circle"
          />
          <span class="has-text-weight-semibold">
            Permission view on: viewing portal as "{{ permissionsPreviewUser }}"
          </span>
        </NrTooltip>
      </div>
      <div class="preview-banner-switch-container">
        <NrButton @click="submitPermission">
          Turn off permissions preview
        </NrButton>
      </div>
    </div>
    <NrModal
      primaryButtonText="Sign in"
      title="Session expired"
      size="small"
      :hasSecondaryButton="false"
      :show="sessionExpired"
      @on-primary-click="directToSignIn"
    >
      <p>
        You have been logged out due to inactivity. Use the button below to sign
        back into the application
      </p>
    </NrModal>
    <!-- This form is submitted programatically to create a POST request -->
    <form
      v-if="hasCivicplusSSOFeature"
      ref="signInForm"
      action="/users/auth/openid_connect"
      method="post"
      class="openid-form"
    >
      <input :name="csrfParam" :value="csrfToken" type="hidden" />
    </form>
    <NrModal
      class="session-warning-modal"
      title="Your session will expire soon"
      type="warning"
      :show="showSessionWarningModal"
      primaryButtonText="Yes, extend my expiration"
      secondaryButtonText="Logout"
      size="small"
      hasSecondaryButton
      @on-primary-click="resetSessionTimeout"
      @on-secondary-click="handleSignoutClick"
      >Your session will expire soon and you will be logged out automatically.
      Do you wish to extend your session? Your session will timeout in
      {{ expirationInMinsAndSecs }}
    </NrModal>
  </header>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { truncate, throttle, uniqBy, isEmpty } from 'lodash'
import { mapActions, mapMutations } from 'vuex'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrButton from '@nextrequestco/components/src/components/NrButton'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import API from '../api.js'
import consumer from '../../utils/action_cable_consumer.js'
import helpers from '../../utils/helpers.js'
import NameAndBadge from 'src/vue_shared/components/NameAndBadge.vue'
import { useCivicPlusSwitchers } from '../../vue_shared/composables/civicPlusSwitcherComponents'
import SupportBanner from 'src/vue_shared/components/SupportBanner.vue'
import MakeRequestMenu from 'src/vue_shared/components/make_request_menu.vue'
import { CustomForm } from 'src/store/globalModule.js'

const genericUsers = [
  {
    value: 'admin',
    label: 'Generic Admin',
    role: 'admin'
  },
  {
    value: 'publisher',
    label: 'Generic Publisher',
    role: 'publisher'
  },
  {
    value: 'staff',
    label: 'Generic Staff',
    role: 'staff'
  },
  {
    value: 'guest',
    label: 'Generic Requester',
    role: 'guest'
  }
]

export type Account = {
  id: number
  name: string
  document_review: boolean
  payments: boolean
  redaction: boolean
  uploaders_feature_flag: boolean
  civicplus_sso_feature_flag: boolean
  cp_organization_id?: string
  cp_access_token?: string
  app_switcher_feature_flag?: boolean
  published_custom_forms?: CustomForm[]
}

type User = {
  id: number
  email_or_name: string
}

export default defineComponent({
  name: 'app-header',
  components: {
    SupportBanner,
    NameAndBadge,
    NrModal,
    NrButton,
    NrIcon,
    NrTooltip,
    NrSelect,
    MakeRequestMenu
  },
  props: {
    account: {
      type: Object as PropType<Account | null>,
      default() {
        return {
          id: -1,
          name: 'NextRequest',
          document_review: false,
          payments: false,
          redaction: false,
          uploaders_feature_flag: false,
          civicplus_sso_feature_flag: false
        }
      }
    },
    user: {
      type: Object as PropType<User | null>,
      default: null
    },
    canManageInternalAdminTools: {
      type: Boolean,
      default: false
    },
    canReadStaffOnly: {
      type: Boolean,
      default: false
    },
    canManageAll: {
      type: Boolean,
      default: false
    },
    canReadAdminOnly: {
      type: Boolean,
      default: false
    },
    canPreviewUser: {
      type: Boolean,
      default: false
    },
    permissionsPreviewUser: {
      type: String,
      default: null
    },
    requestPrettyId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      isMenuToggled: false,
      permissionModal: false,
      selectedPreviewUsers: [],
      filteredPreviewUsers: [],
      sessionExpired: false,
      previewFetchState: {
        page: 0,
        search_text: '',
        end: false,
        fetching: false
      },
      showSessionWarningModal: false,
      expirationInMinsAndSecs: '',
      csrfToken: null,
      subscription: null,
      onSmallScreen: false
    }
  },

  mounted() {
    const smallScreenMatcher = window.matchMedia('(max-width: 769px)')
    this.onSmallScreen = smallScreenMatcher.matches
    smallScreenMatcher.addEventListener('change', (e) => {
      this.onSmallScreen = e.matches
    })

    this.setUpSessionExpiration()
    this.setCsrfTokenFromPage()
    this.subscribeToUserJobChannel()
    if (
      this.account.app_switcher_feature_flag &&
      this.account.cp_organization_id
    ) {
      useCivicPlusSwitchers(
        this.account.cp_organization_id,
        this.account.cp_access_token,
        'cp-appSwitcher'
      )
      useCivicPlusSwitchers(
        this.account.cp_organization_id,
        this.account.cp_access_token,
        'cp-mobile-appSwitcher'
      )
    }
  },

  beforeUnmount() {
    this.unsubscribeToUserJobChannel()
  },

  watch: {
    requestPrettyId(newVal, _oldVal) {
      // Catch up job statuses where request pretty id is required
      if (!newVal || !this.user) return
      this.catchUpOnJobStatuses()
    }
  },

  computed: {
    isRapidReview() {
      return this.$route.name === 'rapid-review'
    },

    isSpa(): boolean {
      return !!this.$router.options.routes.length
    },

    accountSettingsPath() {
      return `/admin/accounts/${this.account.id}`
    },

    userSettingsPath() {
      return `/settings?id=${this.user.id}`
    },

    accountName(): string {
      if (this.account) {
        return truncate(this.account.name, { length: 40 })
      } else {
        return 'NextRequest'
      }
    },

    isAuthenticated(): boolean {
      return !!this.user && !!this.account
    },

    selectedPreviewUserId(): number | null {
      return this.selectedPreviewUsers ? this.selectedPreviewUsers.value : null
    },

    hasCivicplusSSOFeature(): boolean {
      return this.account.civicplus_sso_feature_flag
    },

    csrfParam(): string {
      const csrfEl = document.getElementsByName('csrf-param')
      const param =
        csrfEl.length > 0 ? (csrfEl[0] as HTMLMetaElement).content : ``
      return param
    },

    // On large screens the "Make request" menu should always be on the left.
    // On small screens it should be first if there is only one form, otherwise below the other items.
    makeRequestMenuFirst(): boolean {
      return (
        !this.onSmallScreen ||
        !this.account.published_custom_forms ||
        this.account.published_custom_forms.length <= 1
      )
    }
  },

  methods: {
    isEmpty,
    ...mapActions('rapid_review', [
      'fetchZipFileToDownload',
      'fetchFolders',
      'refreshDocumentReviewList',
      'fetchReviewStateCounts',
      'removeProcessingDocIds'
    ]),

    catchUpOnJobStatuses() {
      const processingJobs = helpers.fetchUserProcessingJobIds(
        this.user.id,
        this.requestPrettyId,
        'rapid_review'
      )
      if (processingJobs && processingJobs.length) {
        this.checkJobStatuses(processingJobs)
      }
    },

    unsubscribeToUserJobChannel() {
      if (consumer.connection.isOpen()) {
        consumer.subscriptions.remove(this.subscription)
      }
    },

    checkJobStatuses(processingJobs) {
      API.sidekiqStatuses({ jids: processingJobs }).then((response) => {
        const jobs = response.data.jobs
        if (jobs && jobs.length) {
          jobs.forEach((job) => {
            const pendingJob = helpers.fetchPendingJobById(
              job.jid,
              this.user.id,
              this.requestPrettyId,
              'rapid_review'
            )
            if (!isEmpty(pendingJob)) {
              const data = {
                status: job.status,
                jobType: pendingJob.jobType,
                jobId: job.jid,
                docIds: pendingJob.docIds,
                prettyId: this.requestPrettyId
              }
              this.setupToastMessage(data)
            }
          })
        }
      })
    },

    subscribeToUserJobChannel() {
      if (!this.user) return
      const self = this
      this.subscription = consumer.subscriptions.create(
        // Subscribe to channel
        {
          channel: 'UserJobChannel',
          user_id: this.user.id
        },
        {
          received(data) {
            const jobData = {
              status: data.status,
              jobType: data.job_type,
              jobId: data.job_id,
              docIds: data.doc_ids,
              prettyId: data.pretty_id || self.requestPrettyId,
              payload: data.payload
            }
            self.$store.state.lastJobMessage = jobData
            self.setupToastMessage(jobData)
          }
        }
      )
    },

    setupToastMessage({ status, jobType, jobId, docIds, prettyId, payload }) {
      if (helpers.backgroundJobComplete(status)) {
        if (jobType === 'bulk_download') {
          this.setupBulkDownload(jobId, docIds?.length, prettyId)
        } else {
          this.setSuccessToastMessage(jobType, docIds?.length, payload)
        }
      } else if (helpers.backgroundJobFailed(status)) {
        this.setFailedToastMessage(jobType, docIds?.length, payload)
      }

      if (
        helpers.backgroundJobComplete(status) ||
        helpers.backgroundJobFailed(status)
      ) {
        if (this.isRapidReview) {
          this.fetchFolders(prettyId).then(() => {
            this.refreshDocumentReviewList()
            this.fetchReviewStateCounts(prettyId)
          })
        }
        if (this.$store) {
          this.removeProcessingDocIds(docIds)
        }
        helpers.removePendingJobById(
          jobId,
          this.user.id,
          prettyId,
          'rapid_review'
        )
      }
    },

    completeToastMessage(jobType: string, numberOfDocuments: number): string {
      switch (jobType) {
        case 'zipfile_creator':
          return `Successfully zipped ${numberOfDocuments} document(s).`
        case 'process_folders':
          return `Successfully moved ${numberOfDocuments} document(s).`
        case 'draft_redaction':
          return `Successfully draft redacted ${numberOfDocuments} document(s).`
        case 'zipfile_reader':
          return `Successfully extracted ${numberOfDocuments} document(s).`
        case 'bulk_download':
          return `Downloading zip file containing ${numberOfDocuments} document(s).`
        case 'bulk_delete':
          return `Successfully deleted ${numberOfDocuments} document(s).`
        case 'mail_reader':
          return `Successfully extracted ${numberOfDocuments} document(s).`
        case 'flatten':
          return `Successfully flattened ${numberOfDocuments} document(s).`
        case 'remove_metadata':
          return `Successfully removed metadata from ${numberOfDocuments} document(s).`
      }
    },

    failedToastMessage(
      jobType: string,
      numberOfDocuments: number
    ): string | null {
      switch (jobType) {
        case 'zipfile_creator':
          return `${numberOfDocuments} document(s) failed to zip.`
        case 'process_folders':
          return `${numberOfDocuments} document(s) failed to move.`
        case 'draft_redaction':
          return `${numberOfDocuments} document(s) failed to draft redact.`
        case 'zipfile_reader':
          return `${numberOfDocuments} document(s) failed to unzip.`
        case 'bulk_download':
          return `Zip file containing ${numberOfDocuments} document(s) failed to download.`
        case 'bulk_delete':
          return `${numberOfDocuments} document(s) failed to delete.`
        case 'mail_reader':
          return `${numberOfDocuments} document failed to extract.`
        case 'flatten':
          return `${numberOfDocuments} document(s) failed to flatten.`
        case 'remove_metadata':
          return `${numberOfDocuments} document(s) failed to remove metadata.`
      }
    },

    setSuccessToastMessage(
      jobType: string,
      numberOfDocuments: number,
      payload: null | { message: string }
    ) {
      const toast =
        payload?.message ||
        this.completeToastMessage(jobType, numberOfDocuments)
      if (toast) {
        this.$toasted.success(toast)
      }
    },

    setFailedToastMessage(
      jobType: string,
      numberOfDocuments: number | undefined,
      payload: null | { message: string }
    ) {
      const toast =
        payload.message || this.failedToastMessage(jobType, numberOfDocuments)
      if (toast) {
        this.$toasted.error(toast)
      }
    },

    fetchZipFile(payload, numOfDocs) {
      this.fetchZipFileToDownload(payload)
        .then(() => {
          const message = this.completeToastMessage('bulk_download', numOfDocs)
          this.$toasted.success(message)
        })
        .catch(() => {
          const message = this.failedToastMessage('bulk_download', numOfDocs)
          this.$toasted.error(message)
        })
    },

    setupBulkDownload(jobId, numOfDocs, prettyId) {
      // Since there is no Vuex store available on the app_header.ts instance, we emit an event to prepare the zip file in app_header.ts when on the non Vue app
      const ssJobId = sessionStorage.getItem(`job_${jobId}`)
      if (ssJobId && jobId === ssJobId) {
        const payload = { jid: jobId, request_id: prettyId }
        this.$store
          ? this.fetchZipFile(payload, numOfDocs)
          : this.$emit('fetch-zip-file-to-download', {
              jid: jobId,
              request_id: prettyId,
              numOfDocs
            })

        helpers.clearJobIdFromSessionStorage(jobId)
      }
    },
    setCsrfTokenFromPage() {
      const csrfEl = document.getElementsByName('csrf-token')
      const token =
        csrfEl.length > 0 ? (csrfEl[0] as HTMLMetaElement).content : ``
      this.csrfToken = token
    },
    setCsrfTokenFromApi() {
      API.getCsrfToken().then((response) => {
        this.csrfToken = response.data.csrf_token
      })
    },
    resetSessionTimeout() {
      API.resetSession().then(() => {
        this.showSessionWarningModal = false
      })
    },
    timeToSessionExpiration() {
      let expirationTime = this.$cookies.get('nextrequest_session_expires')
      const now = new Date()
      expirationTime = new Date(expirationTime)
      const differenceTime = new Date(
        Math.abs(now.getTime() - expirationTime.getTime())
      )
      const minutes = differenceTime.getMinutes()
      const seconds = differenceTime.getSeconds()
      return { minutes, seconds }
    },
    setUpSessionExpiration() {
      const sessionCookiePresent = () =>
        this.$cookies?.isKey('nextrequest_session_expires')

      // If the cookie is not set then the session has no expiration, and so we do not need to check for it.
      if (sessionCookiePresent()) {
        const sessionExpirationInterval = setInterval(() => {
          if (this.user && !sessionCookiePresent()) {
            this.sessionExpired = true
            this.showSessionWarningModal = false
            this.setCsrfTokenFromApi()
            localStorage.clear()
            clearInterval(sessionExpirationInterval)
          } else if (this.user && sessionCookiePresent()) {
            const time = this.timeToSessionExpiration()
            if (time.minutes < 2) {
              const formatSeconds = time.seconds.toString().padStart(2, '0')
              this.expirationInMinsAndSecs = `${time.minutes} min ${formatSeconds} sec`
              this.showSessionWarningModal = true
            } else {
              this.showSessionWarningModal = false
            }
          }
        }, 1000)
      }
    },
    directToSignIn() {
      if (this.hasCivicplusSSOFeature) {
        this.$refs.signInForm.requestSubmit()
      } else {
        window.location.href = '/users/sign_in'
      }
    },
    handleNavigate(event, routerNavFunc) {
      if (this.isSpa) {
        this.handleToggleMenu()
        return routerNavFunc(event)
      }
    },
    getNavbarItemClassName(path) {
      const isActive = window.location.pathname === path.path
      return ['navbar-item', { 'navbar-item--active': isActive }]
    },
    ...mapMutations(['setAbility']),
    submitPermission(): void {
      if (this.isRapidReview && this.selectedPreviewUsers.role === 'guest') {
        this.$toasted.error(
          'The user you have selected does not have access to this page'
        )
        return
      }
      API.setPermissionUserPreview({
        user_id: this.selectedPreviewUserId
      }).then((_response) => {
        window.location.reload()
      })
    },
    setPermissionModal(): void {
      this.permissionModal = true
    },
    searchPreviewUsers: throttle(function (searchText = '') {
      this.fetchPreviewUsers(searchText)
    }, 400),

    setPreviewUsers(staff: object): void {
      this.selectedPreviewUsers = staff
    },
    fetchPreviewUsers(searchText: string = ''): void {
      const newFetchState = { ...this.previewFetchState }
      if (
        this.previewFetchState.search_text === searchText &&
        this.previewFetchState.end
      ) {
        return
      }
      if (this.previewFetchState.search_text === searchText) {
        Object.assign(newFetchState, {
          search_text: searchText,
          page: this.previewFetchState.page + 1,
          fetching: true
        })
      }

      if (this.previewFetchState.search_text !== searchText) {
        Object.assign(newFetchState, {
          search_text: searchText,
          page: 1,
          end: false,
          fetching: true
        })
      }
      const payload = {
        search_text: searchText,
        page_number: newFetchState.page,
        request_pretty_id: this.requestPrettyId
      }
      API.fetchPreviewUsers(payload)
        .then((response) => {
          const res = response.data
          let staffList = []
          if (res.length) {
            staffList = res.map((usr) => {
              return {
                value: usr.id,
                label: usr.name_with_email,
                role: usr.role
              }
            })
          }
          if (this.previewFetchState.search_text !== payload.search_text) {
            this.filteredPreviewUsers = []
            this.filteredPreviewUsers = staffList.concat(genericUsers)
          } else {
            this.filteredPreviewUsers = uniqBy(
              [...this.filteredPreviewUsers, ...staffList, ...genericUsers],
              'value'
            )
          }
          this.previewFetchState = Object.assign(newFetchState, {
            end: staffList.length < 25,
            fetching: false
          })
        })
        .catch(() => {
          this.filteredPreviewUsers = [].concat(genericUsers)
          this.previewFetchState = Object.assign(newFetchState, {
            fetching: false
          })
        })
    },
    loadMorePreviewUsers: throttle(function () {
      this.fetchPreviewUsers(this.previewFetchState.search_text)
    }, 400),

    resetPreviewModal() {
      this.filteredPreviewUsers = []
      this.permissionModal = false
      this.previewFetchState = {
        page: 0,
        search_text: '',
        end: false,
        fetching: false
      }
    },

    handleToggleMenu(): void {
      this.isMenuToggled = !this.isMenuToggled

      if (this.isMenuToggled) {
        this.$refs.menu.classList.add('is-active')
      } else {
        this.$refs.menu.classList.remove('is-active')
      }
    },

    handleSignoutClick(): void {
      this.$emit('on-sign-out')
    },

    focusMainContent() {
      const main = document.getElementById('skip-to-main-content')
      if (main) {
        main.setAttribute('tabindex', '-1')
        main.focus()
      }
    }
  }
})
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.permission-modal {
  .nr-modal__content {
    padding: 1.25em;
  }
  .label {
    color: #09090b;
    display: block;
    font-size: 1rem;
    font-weight: 700;
    background-color: white;
    border: none;
    float: left;
    padding: 0 0 8px 0;
  }
}

.session-warning-modal.nr-modal {
  z-index: 9999;
  position: fixed;
}
.redaction-hover {
  z-index: 1;
}

// AppSwitcher style tweaks based on foundations style overrides
.cp-appSwitcher-MuiButtonBase-root {
  color: $color-gray-100 !important;
  &:focus {
    color: $color-gray-40;
    background-color: $color-gray-30;
  }
}

.cp-appSwitcher-MuiInputBase-input {
  margin-bottom: 0px !important;
}
</style>

<style scoped lang="scss">
@import '../../../styles/globals.scss';
$navbar-breakpoint: $tablet;
@import 'bulma/bulma.sass';
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

.navbar-desktop-app-switcher {
  margin-top: 4px;
  margin-left: 0;
  margin-right: 0;
  padding-left: 0;
  padding-right: 0;
  padding-bottom: $spacing-12;
  margin-bottom: $spacing-16;
}
.navbar-mobile-app-switcher {
  padding-left: 0;
}
:deep(.navbar-icon-desktop) {
  display: none;
  margin-top: auto;
  margin-bottom: auto;
}
:deep(.navbar-icon-mobile) {
  display: flex;
  align-items: center;
  font-size: $spacing-16;
  font-weight: $font-weight-semibold;
  .nr-icon,
  .nr-icon.icon {
    margin-right: $spacing-8;
  }
}
.navbar-user-icon {
  font-size: $spacing-16;
  font-weight: $font-weight-semibold;
  .nr-icon {
    width: 1.75rem;
    height: 1.75rem;
  }
}
@media (min-width: 769px) {
  :deep(.navbar-icon-mobile) {
    display: none;
  }
  :deep(.navbar-icon-desktop) {
    display: block;
  }
}
.button.is-primary {
  background-color: $color-blue-100;
  margin-bottom: 0;
  top: 0;

  &:focus:not(:active) {
    outline: 2px dotted #10163e;
    outline-offset: 4px;
  }
}

.navbar {
  flex-wrap: wrap;
}

.inline-icon {
  display: inline-block;
  vertical-align: middle;
}

.navbar {
  box-shadow: 0 1px 3px 0px rgba(56, 56, 56, 0.16);
}

.navbar,
.navbar-menu {
  background-color: $color-white !important;
  ul {
    display: flex;
    li {
      margin-top: $spacing-16;
    }
  }
}

.navbar-container {
  .navbar-brand {
    flex-shrink: 1;
    padding: $spacing-8 $spacing-24 $spacing-8 $spacing-24;
    background-color: $color-gray-20;
  }
}

a.navbar-item:hover,
.navbar-item:hover,
a.navbar-item:focus,
.navbar-item:focus,
a.navbar-item:active,
.navbar-item:active,
.navbar-item.has-dropdown:active .navbar-link,
.navbar-item.has-dropdown:active a.navbar-link,
.navbar-item.has-dropdown:hover .navbar-link,
.navbar-item.has-dropdown:hover a.navbar-link,
.navbar-item.has-dropdown:focus .navbar-link,
.navbar-item.has-dropdown:focus a.navbar-link {
  background-color: transparent;
}
:deep(.navbar-dropdown) .navbar-item::after {
  display: none;
}

:deep(.navbar-item) {
  .navbar-link:hover,
  a:hover {
    color: inherit;
  }
}

:deep(.navbar-item) {
  &.navbar-title {
    font-size: $font-size-5;
    font-family: 'Inter', 'Open Sans', Arial, sans-serif;
    font-weight: 300;
    letter-spacing: -1px;
    line-height: 100%;
    padding: 4px 0 8px;
  }
  &.navbar-title:hover,
  .navbar-title:focus,
  .navbar-title:active {
    color: $color-blue-110;
  }
}

.navbar-start :deep(.navbar-item) {
  &.navbar-item--active {
    > a {
      border-bottom-color: currentColor;
    }
  }
  > a {
    border-bottom: 2px solid transparent;
    &:hover {
      border-color: currentColor;
    }
  }
}

.navbar-menu {
  font-size: 0.875rem;
}

:deep(.navbar-dropdown) {
  .navbar-item {
    text-transform: none;
  }
}

@media screen and (max-width: 769px) {
  .navbar-container {
    display: block;
    flex-grow: unset;
  }
}

@media screen and (min-width: 769px) {
  .navbar-container {
    display: flex;
    flex-grow: 1;
  }
}

.openid-form {
  display: none;
}

.preview-banner {
  background: $yellow;
  display: flex;
  justify-content: space-between;
  padding: 0.5em;

  display: flex;
  flex-direction: row;
  align-items: center;

  &-title {
    font-size: 20px;
  }
  &-switch-container {
    text-align: right;
    position: relative;
    .icon {
      transform: scale(0.6) translate(0px, 10px);
    }
    .switch {
      position: relative;
    }
  }

  @media screen and (max-width: 800px) {
    & {
      flex-wrap: wrap;

      & > * {
        text-align: center;
      }

      &-spacer {
        display: none;
      }
    }
  }
}

#name-and-badge-png {
  height: 1rem;
}
</style>
